import React, { useState } from "react";
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from "react-icons/fa";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TrialBalanceTable = ({ accounts = [], summary = {} }) => {
  const [collapsedGroups, setCollapsedGroups] = useState({});

  if (!accounts.length) {
    return (
      <div className="tb-empty">
        <p>No accounts found for the selected period.</p>
      </div>
    );
  }

  // ── Expand / Collapse Actions ─────────────────────────
  const isAnyCollapsed = Object.values(collapsedGroups).some((v) => v === true);

  const handleToggleAll = () => {
    if (isAnyCollapsed) {
      setCollapsedGroups({});
    } else {
      const collapsed = {};
      accounts.forEach((acc) => {
        if (acc.is_group) {
          collapsed[acc.id] = true;
        }
      });
      setCollapsedGroups(collapsed);
    }
  };

  const toggleGroup = (id) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // ── Filter Out Collapsed Rows ─────────────────────────
  const visibleAccounts = [];
  let collapseLevel = 999;

  accounts.forEach((acc) => {
    if (acc.level > collapseLevel) {
      return;
    }
    collapseLevel = 999;

    if (collapsedGroups[acc.id]) {
      collapseLevel = acc.level;
    }
    visibleAccounts.push(acc);
  });

  return (
    <div className="tb-table-card">
      <div className="tb-table-header">
        <button className="tb-btn-toggle-all" onClick={handleToggleAll}>
          {isAnyCollapsed ? "↔ Expand All" : "↕ Collapse All"}
        </button>
        <span>{accounts.length} accounts in hierarchy</span>
      </div>

      <div className="tb-table-wrapper">
        <table className="tb-table">
          <thead>
            <tr>
              <th style={{ width: "60px" }}>#</th>
              <th>Account Name</th>
              <th className="num">Debit (Dr)</th>
              <th className="num">Credit (Cr)</th>
              <th className="num">Net Balance</th>
              <th className="center" style={{ width: "90px" }}>Side</th>
            </tr>
          </thead>

          <tbody>
            {visibleAccounts.map((row, idx) => {
              const isDebit = row.balance_side === "Dr";
              const isCollapsed = !!collapsedGroups[row.id];
              const rowClass = row.is_group
                ? `row-group level-${row.level}`
                : `row-leaf ${idx % 2 === 0 ? "row-even" : "row-odd"}`;

              return (
                <tr key={row.id} className={rowClass}>
                  <td className="row-num">{idx + 1}</td>
                  <td
                    className="acct-name"
                    style={{
                      paddingLeft: `${row.level * 20 + 12}px`,
                      cursor: row.is_group ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={() => row.is_group && toggleGroup(row.id)}
                  >
                    {row.is_group ? (
                      <span className="tb-tree-toggle">
                        {isCollapsed ? "▶" : "▼"}{" "}
                        <strong className="group-text">{row.name}</strong>
                      </span>
                    ) : (
                      <span className="leaf-text">{row.name}</span>
                    )}
                  </td>
                  <td className={`num ${row.debit > 0 ? "amount-debit" : "amount-zero"}`}>
                    {row.debit > 0 ? fmt(row.debit) : "—"}
                  </td>
                  <td className={`num ${row.credit > 0 ? "amount-credit" : "amount-zero"}`}>
                    {row.credit > 0 ? fmt(row.credit) : "—"}
                  </td>
                  <td className={`num ${isDebit ? "amount-debit" : "amount-credit"}`}>
                    {fmt(Math.abs(row.net_balance))}
                  </td>
                  <td className="center">
                    <span className={`side-badge ${isDebit ? "badge-dr" : "badge-cr"}`}>
                      {row.balance_side}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr className="totals-row">
              <td colSpan={2}>
                <strong>TOTALS</strong>
              </td>
              <td className="num amount-debit">
                <strong>{fmt(summary.total_debit)}</strong>
              </td>
              <td className="num amount-credit">
                <strong>{fmt(summary.total_credit)}</strong>
              </td>
              <td className="num">
                <strong
                  className={
                    Math.abs(summary.difference || 0) < 0.01
                      ? "balanced-ok"
                      : "balanced-err"
                  }
                >
                  {fmt(Math.abs(summary.difference || 0))}
                </strong>
              </td>
              <td className="center">
                {summary.is_balanced ? (
                  <span className="balanced-badge balanced-ok-badge"><FaCheckCircle className="me-1" /> Balanced</span>
                ) : (
                  <span className="balanced-badge balanced-err-badge"><FaTimesCircle className="me-1" /> Off</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="tb-note">
        <span className="tb-note-icon"><FaInfoCircle style={{ color: "#0284c7" }} /></span>
        <div>
          <strong>About Hierarchical Trial Balance:</strong> Root ledger categories (Assets, Liabilities, Equity, Revenue, Expenses) roll up all sub-ledger transaction balances recursively. Overall totals are calculated from root categories to avoid double-counting.
        </div>
      </div>
    </div>
  );
};

export default TrialBalanceTable;
